/**
 * Mock API client for the invoicing demo. Drops in for the real backend client —
 * preserves the same export surface so call sites don't need to change.
 *
 * Architecture:
 *  - apiClient.get/post/put/patch/delete -> match URL against handler table, return { data }
 *  - apiClient.fetch -> Response-like wrapper for legacy direct-fetch call sites
 *  - simulateLatency adds 200-600ms of jitter to feel "real"
 */

import { mockStore, type MockInvoice, type MockMatch, type MockCounterpart } from '@/mocks/store'

const autoMatchJobs = new Map<string, { matched: number; total: number }>()
import { accountsFixture } from '@/mocks/fixtures/accounts'
import { dealNamesFixture } from '@/mocks/fixtures/dealNames'
import { accAssignmentsFixture } from '@/mocks/fixtures/accAssignments'
import { unitsOfMeasureFixture } from '@/mocks/fixtures/unitsOfMeasure'

class ApiError extends Error {
  status: number
  data?: unknown
  constructor(status: number, message: string, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms))
const simulateLatency = (min = 200, max = 600) => sleep(min + Math.random() * (max - min))

// ---------------------------------------------------------------------------
// Handler types & helpers
// ---------------------------------------------------------------------------

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RouteMatch {
  params: Record<string, string>
  query: URLSearchParams
}

type Handler = (match: RouteMatch, body: unknown, raw: { method: Method; url: string }) => unknown | Promise<unknown>

interface Route {
  method: Method
  pattern: RegExp
  paramNames: string[]
  handler: Handler
}

function compilePattern(pattern: string): { regex: RegExp; paramNames: string[] } {
  const paramNames: string[] = []
  const regex = new RegExp(
    '^' +
      pattern.replace(/:([A-Za-z_]+)/g, (_, name) => {
        paramNames.push(name)
        return '([^/?]+)'
      }) +
      '/?$',
  )
  return { regex, paramNames }
}

const routes: Route[] = []
function on(method: Method, pattern: string, handler: Handler) {
  const { regex, paramNames } = compilePattern(pattern)
  routes.push({ method, pattern: regex, paramNames, handler })
}

function findRoute(method: Method, pathname: string): { route: Route; params: Record<string, string> } | null {
  for (const route of routes) {
    if (route.method !== method) continue
    const m = pathname.match(route.pattern)
    if (m) {
      const params: Record<string, string> = {}
      route.paramNames.forEach((name, i) => (params[name] = decodeURIComponent(m[i + 1])))
      return { route, params }
    }
  }
  return null
}

function parseUrl(url: string): { pathname: string; query: URLSearchParams } {
  // Inputs may be /api/v1/foo, /foo, or http://... — normalise to a pathname
  const full = url.startsWith('http') ? new URL(url) : new URL(url, 'http://mock.local')
  let pathname = full.pathname
  if (pathname.startsWith('/api/v1')) pathname = pathname.slice('/api/v1'.length) || '/'
  return { pathname, query: full.searchParams }
}

// ---------------------------------------------------------------------------
// AUTH
// ---------------------------------------------------------------------------

on('POST', '/auth/login', async (_m, body) => {
  const b = (body as { email?: string; password?: string }) || {}
  const email = b.email || 'demo@superstate.one'
  const name = email
    .split('@')[0]
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
  mockStore.currentUser = { ...mockStore.currentUser, email, full_name: name }
  return {
    access_token: 'mock-access-token',
    refresh_token: 'mock-refresh-token',
    token_type: 'bearer',
    user: mockStore.currentUser,
  }
})
on('POST', '/auth/logout', async () => ({ ok: true }))
on('POST', '/auth/refresh', async () => ({ access_token: 'mock-access-token' }))
on('GET', '/auth/me', async () => mockStore.currentUser)
on('GET', '/auth/me/preferences', async () => ({
  learning_preference: mockStore.currentUser.learning_preference,
  default_client_id: 'client-default',
}))
on('PATCH', '/auth/me/preferences', async (_m, body) => {
  Object.assign(mockStore.currentUser, body || {})
  return { ok: true }
})
on('PUT', '/auth/me/preferences', async (_m, body) => {
  Object.assign(mockStore.currentUser, body || {})
  return { ok: true }
})
on('POST', '/auth/change-password', async () => ({ ok: true }))
on('POST', '/auth/forgot-password', async () => ({ ok: true }))
on('POST', '/auth/reset-password', async () => ({ ok: true }))
on('GET', '/auth/password-policy', async () => ({
  min_length: 8,
  require_upper: false,
  require_lower: false,
  require_digit: false,
  require_special: false,
}))

// ---------------------------------------------------------------------------
// USERS
// ---------------------------------------------------------------------------
on('GET', '/users/mirage-users', async () => ({
  database_name: 'MirageFirm4',
  users: [
    { id: 1, username: 'demo', full_name: 'Demo User', is_active: true },
    { id: 2, username: 'admin', full_name: 'Admin', is_active: true },
  ],
}))
on('GET', '/users/:id', async () => mockStore.currentUser)
on('PUT', '/users/:id', async (_m, body) => ({ message: 'updated', user: { ...mockStore.currentUser, ...(body as object) } }))
on('GET', '/admin/users', async () => ({ users: [mockStore.currentUser], total: 1 }))
on('PUT', '/admin/users/:id/role', async () => mockStore.currentUser)
on('PATCH', '/admin/users/:id', async () => mockStore.currentUser)
on('POST', '/admin/users/:id/force-password-change', async () => ({ ok: true }))
on('POST', '/admin/users/:id/reset-password', async () => ({ ok: true, temporary_password: 'demo1234' }))
on('POST', '/admin/users/:id/status', async () => ({ ok: true }))
on('POST', '/admin/users/:id/unlock', async () => ({ ok: true }))
on('GET', '/admin/audit-logs', async () => ({ logs: [], total: 0 }))

// ---------------------------------------------------------------------------
// CLIENTS
// ---------------------------------------------------------------------------
on('GET', '/clients', async () => ({
  clients: mockStore.clients,
  total: mockStore.clients.length,
  limit: 1000,
  offset: 0,
}))
on('GET', '/clients/', async () => ({
  clients: mockStore.clients,
  total: mockStore.clients.length,
  limit: 1000,
  offset: 0,
}))
on('GET', '/clients/:id', async ({ params }) => {
  const c = mockStore.clients.find((c) => c.id === params.id) || mockStore.clients[0]
  return { client: c }
})
on('POST', '/clients', async (_m, body) => {
  const b = body as Partial<MockClientLike>
  const newClient = {
    id: `client-${Date.now()}`,
    name: b.name || 'New Client',
    status: 'active',
    user_id: 'mock-user',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ...b,
  } as (typeof mockStore.clients)[number]
  mockStore.clients.push(newClient)
  return { message: 'created', client: newClient }
})
on('PATCH', '/clients/:id', async ({ params }, body) => {
  const c = mockStore.clients.find((c) => c.id === params.id)
  if (c) Object.assign(c, body || {})
  return { message: 'updated', client: c }
})
on('DELETE', '/clients/:id', async () => ({ message: 'deleted' }))
type MockClientLike = { name?: string; status?: string }

// ERP reference data
on('GET', '/accounts', async () => ({ accounts: accountsFixture }))
on('GET', '/clients/:id/acc-assignment', async () => ({ acc_assignments: accAssignmentsFixture }))
on('GET', '/clients/:id/deal-names', async () => ({ deal_names: dealNamesFixture }))
on('GET', '/clients/:id/units-of-measure', async () => ({ units_of_measure: unitsOfMeasureFixture }))

// Analytic parts for a given (client, acc_assignment). Returns a small fixed
// set so every line item renders a 2-level analytic-part dropdown chain.
on('GET', '/clients/:id/acc-assignment/:aaId/analytic-parts', async ({ params }) => {
  const aaId = Number(params.aaId)
  // The account code attached to the assignment differs by direction; the
  // important thing is each line item gets a populated definition.
  const isSale = aaId >= 200 && aaId < 300
  const accountCode = isSale ? '702' : '6021'
  const accountName = isSale ? 'Revenue from services' : 'External services - SaaS / cloud'
  return {
    acc_assignment_id: aaId,
    analytic_parts: [
      {
        account_id: aaId * 10,
        account_code: accountCode,
        account_name: accountName,
        analytic_part_id: 1,
        acc_obj_id: 1,
        directions: isSale ? ['credit'] : ['debit'],
        lookup_nomen_type_fk: 1,
        options: [
          { value: '001', label: 'IT / Cloud services' },
          { value: '002', label: 'Marketing & sales' },
          { value: '003', label: 'General & admin' },
        ],
        fields: [
          {
            field_id: 1,
            field_name: 'business_unit',
            label: 'Business unit',
            row_num: 1,
            lookup_nomen_type_fk: 1,
            options: [
              { value: '001', label: 'IT / Cloud services' },
              { value: '002', label: 'Marketing & sales' },
              { value: '003', label: 'General & admin' },
            ],
            all_options: [],
          },
          {
            field_id: 2,
            field_name: 'cost_center',
            label: 'Cost center',
            row_num: 2,
            lookup_nomen_type_fk: 2,
            options: [
              { value: '101', label: 'External SaaS' },
              { value: '102', label: 'Hosting & infra' },
              { value: '103', label: 'Tooling' },
            ],
            all_options: [],
          },
        ],
        combinations: [],
      },
    ],
  }
})

// Counterparties for an invoice — used by the vendor/customer dropdown. We
// surface a small list of candidates including the one already selected so
// the field shows a value instead of "Select counterparty / No options".
on('GET', '/invoices/:id/counterparties', async ({ params }) => {
  const inv = mockStore.getInvoice(params.id)
  const candidates = inv?.counterparty
    ? [
        {
          id: inv.counterparty.id,
          name: inv.counterparty.name,
          code: inv.counterparty.code,
          vat_code: inv.counterparty.vat_code,
          address: inv.counterparty.address,
          country: inv.counterparty.country,
          score: 0.98,
        },
      ]
    : []
  // Always include a handful of additional Mirage suppliers so the search
  // dropdown isn't a list of one.
  const extras = [
    { id: 5001, name: 'Hetzner Online GmbH', code: 'HETZ', vat_code: 'DE812871812', country: 'DE' },
    { id: 5002, name: 'Paddle.com Market Ltd (ScreenshotOne)', code: 'PADDLE-SS', vat_code: 'GB229472108', country: 'GB' },
    { id: 5003, name: 'Supabase Pte Ltd', code: 'SUPABASE', vat_code: '202210234E', country: 'SG' },
    { id: 5004, name: 'Paddle.com Market Limited', code: 'PADDLE-GB', vat_code: 'GB229472108', country: 'GB' },
    { id: 5005, name: 'Paddle.com Inc (US)', code: 'PADDLE-US', vat_code: '47-3580775', country: 'US' },
  ]
  const seen = new Set(candidates.map((c) => c.id))
  for (const e of extras) {
    if (!seen.has(e.id)) candidates.push(e as typeof candidates[number])
  }
  return { counterparties: candidates }
})
on('PATCH', '/invoices/:id/counterparty', async ({ params }, body) => {
  const inv = mockStore.getInvoice(params.id)
  const b = (body as { mirage_counterparty_id?: number | null }) || {}
  if (inv) {
    inv.mirage_counterparty_id = b.mirage_counterparty_id ?? undefined
  }
  return { ok: true, counterparty: inv?.counterparty ?? null }
})

// ---------------------------------------------------------------------------
// INVOICES
// ---------------------------------------------------------------------------

// Mirrors backend rule: 'complete' when every erp line has an acc_assignment;
// 'manual' when one or more are missing/cleared. Drives the "без контировка"
// badge on the processing list.
function computeKontirovkaStatus(
  lines: MockInvoice['erp_line_items']
): 'complete' | 'manual' {
  if (!lines || lines.length === 0) return 'manual'
  return lines.every((l) => l.acc_assignment != null) ? 'complete' : 'manual'
}

function toApiInvoice(i: MockInvoice) {
  const client = mockStore.clients[0]

  // Flatten header fields (vendor_*, customer_*, payment_terms, message_type, …)
  const headerFields = Object.entries(i.extracted).map(([k, v], idx) => ({
    id: `${i.id}-f${idx}`,
    field_name: k,
    field_value: String(v ?? ''),
    confidence_score: 0.9,
    is_validated: false,
    is_corrected: false,
  }))

  // Expand line items → flattened extracted fields keyed by line_item_group.
  // The review screen groups any field starting with `line_item_` by its
  // `line_item_group` to build LineItemRow entries.
  const lineItems = i.line_items ?? []
  const lineItemFields = lineItems.flatMap((li, lineIdx) => {
    const base = `${i.id}-li${lineIdx}`
    return [
      {
        id: `${base}-desc`,
        field_name: 'line_item_description',
        field_value: li.description,
        confidence_score: 0.92,
        is_validated: false,
        is_corrected: false,
        line_item_group: li.group,
      },
      {
        id: `${base}-unit`,
        field_name: 'line_item_unit_of_measure',
        field_value: li.unit ?? 'pcs',
        confidence_score: 0.9,
        is_validated: false,
        is_corrected: false,
        line_item_group: li.group,
      },
    ]
  })

  // Build a currency_representation for the active currency. The Review screen
  // looks up qty/unit_price/line_total/vat_rate from the representation for
  // each line_item_group, not from extractedData.
  const currency = i.extracted.currency || 'EUR'
  const totalAmount = i.extracted.total_amount ?? 0
  const subtotal = i.extracted.subtotal ?? 0
  const taxAmount = i.extracted.tax_amount ?? 0
  const currencyRepresentation = {
    id: `${i.id}-repr`,
    invoice_id: i.id,
    currency_code: currency,
    source: 'extracted' as const,
    is_primary: true,
    subtotal: subtotal.toFixed(2),
    tax_amount: taxAmount.toFixed(2),
    total_amount: totalAmount.toFixed(2),
    exchange_rate: null,
    rate_date: null,
    calculated_from_currency: null,
    created_at: i.uploaded_at,
    updated_at: i.processed_at ?? i.uploaded_at,
    line_items: lineItems.map((li) => ({
      line_item_group: li.group,
      quantity: li.quantity,
      unit_price: li.unit_price,
      line_total: li.line_total,
      line_tax: li.line_tax ?? null,
      vat_rate: li.vat_rate,
      discount_percent: li.discount_percent ?? null,
      discount_amount: li.discount_amount ?? null,
    })),
  }

  // Field options that the Review screen reads from additional_metadata.field_options.
  // message_type — Mirage NomenType for Bulgarian invoice document types.
  // document_subject — additional_remarks dropdown.
  const messageTypeOptions = [
    { value: '01', label: '01 — Фактура' },
    { value: '02', label: '02 — Дебитно известие' },
    { value: '03', label: '03 — Кредитно известие' },
    { value: '81', label: '81 — Протокол по чл. 117 ЗДДС' },
    { value: '82', label: '82 — Протокол при ВОП' },
    { value: '12', label: '12 — Касов бон' },
  ]
  const documentSubjectOptions = [
    { value: 'Cloud hosting услуги за м.04 2026', label: 'Cloud hosting услуги за м.04 2026' },
    { value: 'API screenshot subscription - monthly', label: 'API screenshot subscription - monthly' },
    { value: 'Database & backend infrastructure - м.04 2026', label: 'Database & backend infrastructure - м.04 2026' },
    { value: 'Payouts - GB sales (March 2026)', label: 'Payouts - GB sales (March 2026)' },
    { value: 'Payouts - US sales (March 2026)', label: 'Payouts - US sales (March 2026)' },
    { value: 'Remittance advice for payout 220820', label: 'Remittance advice for payout 220820' },
  ]
  // Make sure the actual remark on this invoice is selectable even if not in the
  // shared list above.
  const currentRemark = i.extracted.additional_remarks
  if (currentRemark && !documentSubjectOptions.some((o) => o.value === currentRemark)) {
    documentSubjectOptions.push({ value: currentRemark, label: currentRemark })
  }

  return {
    id: i.id,
    original_filename: i.original_filename,
    file_path: i.file_path,
    file_size: i.file_size,
    mime_type: i.mime_type,
    status: i.status,
    overall_confidence: i.overall_confidence,
    invoice_type: i.invoice_type,
    invoice_date: i.invoice_date,
    mirage_invoice_number: i.extracted.mirage_invoice_number,
    uploaded_at: i.uploaded_at,
    processed_at: i.processed_at,
    error_message: i.error_message,
    client: {
      id: client.id,
      name: client.name,
      status: client.status,
      mssql_client_id: client.mssql_client_id,
      vat_code: client.vat_code,
    },
    counterparty: i.counterparty ?? null,
    mirage_counterparty_id: i.mirage_counterparty_id ?? null,
    active_currency_code: currency,
    currency_representations: [currencyRepresentation],
    extracted_fields: [...headerFields, ...lineItemFields],
    additional_metadata: {
      counterparty: i.counterparty
        ? {
            selected_candidate: {
              id: i.counterparty.id,
              name: i.counterparty.name,
              code: i.counterparty.code,
              vat_code: i.counterparty.vat_code,
              address: i.counterparty.address,
              country: i.counterparty.country,
            },
            candidates: [
              {
                id: i.counterparty.id,
                name: i.counterparty.name,
                code: i.counterparty.code,
                vat_code: i.counterparty.vat_code,
                address: i.counterparty.address,
                country: i.counterparty.country,
                score: 0.98,
              },
            ],
          }
        : undefined,
      field_options: {
        message_type: messageTypeOptions,
        document_subject: documentSubjectOptions,
      },
      erp: {
        line_items: i.erp_line_items ?? [],
      },
    },
    kontirovka_status: computeKontirovkaStatus(i.erp_line_items),
    mirage_followup_done: false,
    deal_name_id: i.deal_name_id,
    account_assignment_id: i.account_assignment_id,
  }
}

on('GET', '/invoices/', async (_m) => {
  const items = mockStore.invoices.map(toApiInvoice)
  return { invoices: items, items, total: items.length, skip: 0, limit: 100 }
})
on('GET', '/invoices/:id', async ({ params }) => {
  const inv = mockStore.getInvoice(params.id)
  if (!inv) throw new ApiError(404, 'Invoice not found')
  const api = toApiInvoice(inv)
  return {
    ...api,
    invoice: api,
    extracted_data: api.extracted_fields,
    currency_representations: api.currency_representations,
    currency_rate_info: null,
  }
})
on('PATCH', '/invoices/:id', async ({ params }, body) => {
  mockStore.updateInvoice(params.id, (body as Partial<MockInvoice>) || {})
  return toApiInvoice(mockStore.getInvoice(params.id)!)
})
on('PUT', '/invoices/:id', async ({ params }, body) => {
  mockStore.updateInvoice(params.id, (body as Partial<MockInvoice>) || {})
  return toApiInvoice(mockStore.getInvoice(params.id)!)
})
on('DELETE', '/invoices/:id', async ({ params }) => {
  mockStore.removeInvoice(params.id)
  return { message: 'deleted' }
})
on('PATCH', '/invoices/:id/status', async ({ params }, body) => {
  mockStore.updateInvoice(params.id, (body as Partial<MockInvoice>) || {})
  return toApiInvoice(mockStore.getInvoice(params.id)!)
})
on('GET', '/invoices/:id/download', async ({ params }) => {
  const inv = mockStore.getInvoice(params.id)
  if (!inv) throw new ApiError(404, 'not found')
  return { download_url: inv.blob_url || inv.file_path }
})
on('POST', '/invoices/:id/reprocess', async () => ({ ok: true }))
on('POST', '/invoices/:id/mirage-followup', async () => ({ ok: true }))
on('POST', '/invoices/:id/return-to-review', async () => ({ ok: true }))
on('GET', '/invoices/:id/pending-learnings', async () => [])
on('POST', '/invoices/reprocess/bulk', async () => ({ ok: true, queued: 0 }))
on('PUT', '/invoices/:invId/fields/:fieldId', async () => ({ ok: true }))
on('POST', '/invoices/batch-export', async () => ({ ok: true }))
on('POST', '/invoices/batch-export-excel', async () => ({ ok: true }))

// Upload — handled separately in fetch() to deal with FormData and simulate the lifecycle
on('POST', '/invoices/upload', async () => ({ ok: true, job_id: 'mock-job' }))

// ---------------------------------------------------------------------------
// DASHBOARD-LIKE BUCKETS (empty / sensible defaults so screens don't crash)
// ---------------------------------------------------------------------------
on('GET', '/ai-instructions/', async () => [])
on('POST', '/ai-instructions/', async (_m, body) => ({ id: 'ai-1', ...(body as object) }))
on('PUT', '/ai-instructions/:id', async (_m, body) => ({ id: 'ai-1', ...(body as object) }))
on('DELETE', '/ai-instructions/:id', async () => undefined)
on('PATCH', '/ai-instructions/reorder', async () => ({ status: 'ok' }))

on('GET', '/system-prompts/', async () => [])
on('GET', '/system-prompts/:key', async ({ params }) => ({ key: params.key, content: '', is_default: true }))
on('PUT', '/system-prompts/:key', async ({ params }, body) => ({ key: params.key, ...(body as object) }))
on('POST', '/system-prompts/:key/reset', async ({ params }) => ({ key: params.key, content: '' }))

on('GET', '/payment-term-mappings/', async () => ({ items: [], total: 0 }))
on('POST', '/payment-term-mappings/', async (_m, body) => ({ id: 'ptm-1', ...(body as object) }))
on('PUT', '/payment-term-mappings/:id', async (_m, body) => ({ id: 'ptm-1', ...(body as object) }))
on('DELETE', '/payment-term-mappings/:id', async () => undefined)

on('GET', '/vendor-hints/', async () => ({ items: [], total: 0 }))
on('POST', '/vendor-hints/', async (_m, body) => ({ id: 'vh-1', ...(body as object) }))
on('PUT', '/vendor-hints/:id', async (_m, body) => ({ id: 'vh-1', ...(body as object) }))
on('DELETE', '/vendor-hints/:id', async () => undefined)

on('GET', '/classification-rules/', async () => ({ items: [], total: 0 }))
on('PUT', '/classification-rules/:id', async (_m, body) => ({ id: 'cr-1', ...(body as object) }))
on('GET', '/classification-rules/:id/versions', async () => [])

on('GET', '/counterparty-mappings/', async () => ({ items: [], total: 0 }))
on('PUT', '/counterparty-mappings/:id', async () => ({ ok: true }))
on('DELETE', '/counterparty-mappings/:id', async () => undefined)

on('GET', '/merge-patterns/', async () => ({ items: [], total: 0 }))
on('PUT', '/merge-patterns/:id', async () => ({ ok: true }))
on('DELETE', '/merge-patterns/:id', async () => undefined)

on('GET', '/client-identifications/', async () => ({ items: [], total: 0 }))
on('GET', '/client-identifications/:id', async () => ({}))
on('POST', '/client-identifications/confirm', async () => ({ ok: true }))

// Reports / Conversations
on('GET', '/reports/conversations', async () => mockStore.conversations)
on('GET', '/reports/conversations/:id', async ({ params }) => {
  const c = mockStore.conversations.find((c) => c.id === params.id)
  return c || { id: params.id, title: 'Demo conversation', messages: [], created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
})
on('PATCH', '/reports/conversations/:id', async ({ params }, body) => {
  const c = mockStore.conversations.find((c) => c.id === params.id)
  if (c) Object.assign(c, body || {})
  return c || { id: params.id, ...(body as object) }
})
on('DELETE', '/reports/conversations/:id', async ({ params }) => {
  mockStore.conversations = mockStore.conversations.filter((c) => c.id !== params.id)
  return undefined
})
on('GET', '/reports/conversations/count', async () => ({ count: mockStore.conversations.length }))

// Currency
on('GET', '/currency-definitions', async () => ({
  common: [
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
  ],
  other: [
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  ],
  all: [
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  ],
}))
on('GET', '/currency/rates', async () => [])
on('POST', '/currency/rates/manual', async (_m, body) => ({ id: 'cr-1', ...(body as object) }))
on('POST', '/currency-rates/rates/fetch', async () => ({ ok: true, fetched: 0 }))

// ---------------------------------------------------------------------------
// RECONCILIATION (legacy Saldo endpoints)
// ---------------------------------------------------------------------------
on('GET', '/reconciliation/transactions', async () => ({
  transactions: mockStore.bankTransactions.map((t) => ({
    id: t.id,
    source: 'bank',
    description: t.description,
    reference: t.reference,
    amount: t.amount,
    booking_ts: t.booking_ts,
    counterparty_name: t.counterparty_name,
    currency: t.currency,
    client_id: 'client-default',
    client_name: 'Гремлинс Лабс ООД',
  })),
  total: mockStore.bankTransactions.length,
  company_name: 'Гремлинс Лабс ООД',
}))
on('POST', '/reconciliation/upload', async () => ({
  message: 'uploaded',
  file_id: 'mock-file',
  job_id: 'mock-job',
  filename: 'statement.csv',
  status_url: '/reconciliation/upload/mock-file/status',
  processing_status: 'completed',
}))
on('GET', '/reconciliation/upload/:fileId/status', async () => ({
  file_id: 'mock-file',
  filename: 'statement.csv',
  status: 'completed' as const,
  message: 'done',
  transactions_count: mockStore.bankTransactions.length,
  uploaded_at: new Date().toISOString(),
}))
on('GET', '/reconciliation/upload/jobs/:jobId/status', async () => ({
  job_id: 'mock-job',
  status: 'completed',
  progress: 100,
  current_step: 'done',
}))
on('GET', '/reconciliation/files/detailed', async () => ({
  files: [
    {
      id: 'file-april-2026',
      original_filename: '2026.04-dsk-bank.csv',
      mime_type: 'text/csv',
      size_bytes: 4200,
      source: 'manual',
      ingested_at: '2026-04-30T12:00:00Z',
      client_id: 'client-default',
      client_name: 'Гремлинс Лабс ООД',
      transaction_count: mockStore.bankTransactions.length,
      status: 'completed' as const,
      has_committed_links: false,
      committed_link_count: 0,
    },
  ],
  total: 1,
  skip: 0,
  limit: 100,
}))
on('GET', '/reconciliation/files', async () => ({ files: [] }))
on('GET', '/reconciliation/files/:id/delete-check', async () => ({
  can_delete: true,
  has_committed_links: false,
  committed_link_count: 0,
  transaction_count: 0,
  warnings: [],
}))
on('DELETE', '/reconciliation/files/:id', async () => ({
  deleted: true,
  file_id: 'mock-file',
  transactions_deleted: 0,
  warnings: [],
}))
on('POST', '/reconciliation/files/:id/reprocess', async () => ({
  success: true,
  message: 'reprocessed',
  transactions_deleted: 0,
  warnings: [],
}))

// Go recon backend (called via direct fetch to localhost:8081) — mocked in fetch override
// ---------------------------------------------------------------------------
// Processing
// ---------------------------------------------------------------------------
on('GET', '/processing/jobs/:id/status', async ({ params }) => ({
  job_id: params.id,
  status: 'completed',
  progress: 100,
  current_step: 'done',
}))

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------
async function dispatch(method: Method, url: string, body: unknown): Promise<unknown> {
  await simulateLatency()
  const { pathname, query } = parseUrl(url)
  const found = findRoute(method, pathname)
  if (!found) {
    console.warn(`[mock-api] Unhandled ${method} ${pathname}`)
    // Return empty-ish data so screens don't explode
    if (method === 'GET') return Array.isArray(body) ? [] : {}
    return { ok: true }
  }
  const result = await found.route.handler({ params: found.params, query }, body, { method, url })
  return result
}

// ---------------------------------------------------------------------------
// Go-backend recon dispatcher (paths starting with /api/v1/reconciliation/*)
// Called via direct fetch from useReconApi.ts
// ---------------------------------------------------------------------------
function reconBackendHandle(method: string, pathname: string, body?: unknown): unknown {
  // Bank accounts
  if (method === 'GET' && /\/clients\/[^/]+\/bank-accounts$/.test(pathname)) {
    return [
      { id: 502, code: '502', sub_code: '1', name: 'Bank account BGN - main', has_analytic: false },
      { id: 503, code: '503', sub_code: '1', name: 'Bank account EUR', has_analytic: false },
    ]
  }
  // Chart of accounts (for CounterpartEditor / PostTab / SplitTab).
  // Use a stable numeric id derived from the code so seeded counterparts
  // (which reference accounts by code, e.g. 401/503) can resolve.
  if (method === 'GET' && /\/clients\/[^/]+\/accounts$/.test(pathname)) {
    return accountsFixture.map((a) => {
      const [code, sub] = a.code.split('-')
      const id = parseInt(`${code}${sub ?? ''}`, 10)
      return {
        id,
        code,
        sub_code: sub ?? '',
        name: a.name,
        has_analytic: false,
      }
    })
  }
  // Account analytics
  if (method === 'GET' && /\/accounts\/[0-9]+\/analytics$/.test(pathname)) {
    return { account_id: 502, analytic_part_id: 1, fields: [] }
  }
  // Matches list
  if (method === 'GET' && /\/clients\/[^/]+\/matches/.test(pathname)) {
    return mockStore.matches.slice().sort((a, b) => b.created_at.localeCompare(a.created_at))
  }
  // Single match
  if (method === 'GET' && /\/matches\/[^/]+$/.test(pathname)) {
    const id = pathname.split('/').pop()!
    return mockStore.matches.find((m) => m.id === id) ?? null
  }
  // Confirm match
  if (method === 'POST' && /\/matches\/[^/]+\/confirm$/.test(pathname)) {
    const id = pathname.split('/').slice(-2)[0]
    const m = mockStore.matches.find((x) => x.id === id)
    if (m) {
      m.state = 'confirmed'
      m.confirmed_at = new Date().toISOString()
    }
    return m
  }
  // Reject match
  if (method === 'POST' && /\/matches\/[^/]+\/reject$/.test(pathname)) {
    const id = pathname.split('/').slice(-2)[0]
    const m = mockStore.matches.find((x) => x.id === id)
    if (m) m.state = 'rejected'
    return m
  }
  // Reverse match
  if (method === 'POST' && /\/matches\/[^/]+\/reverse$/.test(pathname)) {
    const id = pathname.split('/').slice(-2)[0]
    const m = mockStore.matches.find((x) => x.id === id)
    if (m) {
      const hardDelete = (body as { hard_delete?: boolean } | undefined)?.hard_delete
      if (hardDelete) {
        mockStore.matches = mockStore.matches.filter((x) => x.id !== id)
      } else {
        m.state = 'reversed'
      }
    }
    return m
  }
  // Commit matches → state='committed'
  if (method === 'POST' && /\/matches\/commit$/.test(pathname)) {
    const ids = (body as { match_ids?: string[] } | undefined)?.match_ids ?? []
    let committed = 0
    for (const id of ids) {
      const m = mockStore.matches.find((x) => x.id === id)
      if (m) {
        m.state = 'committed'
        m.committed_at = new Date().toISOString()
        committed++
      }
    }
    return { committed, failed: 0, errors: [] }
  }
  // Update match counterparts (PATCH)
  if (method === 'PATCH' && /\/matches\/[^/]+$/.test(pathname)) {
    const id = pathname.split('/').pop()!
    const m = mockStore.matches.find((x) => x.id === id)
    if (m && body && typeof body === 'object') {
      const b = body as { match_kind?: MockMatch['match_kind']; counterparts?: Array<Partial<MockCounterpart>> }
      if (b.match_kind) m.match_kind = b.match_kind
      if (b.counterparts) {
        m.counterparts = b.counterparts.map((cp, idx) => ({
          id: `cp-${id}-${idx}`,
          counterpart_kind: cp.counterpart_kind ?? 'gl_account_post',
          dt_account_id: cp.dt_account_id ?? 0,
          ct_account_id: cp.ct_account_id ?? 0,
          dt_apx_id: cp.dt_apx_id,
          ct_apx_id: cp.ct_apx_id,
          amount: cp.amount ?? '0',
          note: cp.note,
          doc_nom: cp.doc_nom,
          doc_date: cp.doc_date,
        }))
      }
    }
    return m
  }
  // Create new match (Match/Post/Split/Transfer tabs) — state='confirmed'
  if (method === 'POST' && /\/matches$/.test(pathname)) {
    const b = body as { transaction_id?: string; match_kind?: MockMatch['match_kind']; mirage_bank_account_id?: number; counterparts?: Array<Partial<MockCounterpart>> } | undefined
    const txnId = b?.transaction_id ?? ''
    const newMatch: MockMatch = {
      id: `match-${Date.now()}`,
      transaction_id: txnId,
      state: 'confirmed',
      match_kind: b?.match_kind ?? 'gl_post',
      mirage_bank_account_id: b?.mirage_bank_account_id ?? 503,
      counterparts: (b?.counterparts ?? []).map((cp, idx) => ({
        id: `cp-new-${idx}`,
        counterpart_kind: cp.counterpart_kind ?? 'gl_account_post',
        dt_account_id: cp.dt_account_id ?? 0,
        ct_account_id: cp.ct_account_id ?? 0,
        dt_apx_id: cp.dt_apx_id,
        ct_apx_id: cp.ct_apx_id,
        amount: cp.amount ?? '0',
        note: cp.note,
        doc_nom: cp.doc_nom,
        doc_date: cp.doc_date,
      })),
      confirmed_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
    }
    // Replace any existing non-terminal match for this txn
    mockStore.matches = mockStore.matches.filter(
      (x) => x.transaction_id !== txnId || x.state === 'committed' || x.state === 'rejected' || x.state === 'reversed'
    )
    mockStore.matches.push(newMatch)
    return newMatch
  }
  // Link existing Mirage entry — state='confirmed'
  if (method === 'POST' && /\/matches\/link$/.test(pathname)) {
    const b = body as { transaction_id?: string; mirage_bank_account_id?: number; acc_rec_d_id?: number } | undefined
    const txnId = b?.transaction_id ?? ''
    const newMatch: MockMatch = {
      id: `match-link-${Date.now()}`,
      transaction_id: txnId,
      state: 'confirmed',
      match_kind: 'manual_link',
      mirage_bank_account_id: b?.mirage_bank_account_id ?? 503,
      counterparts: [
        {
          id: `cp-link-0`,
          counterpart_kind: 'mirage_open_item',
          dt_account_id: 503,
          ct_account_id: 401,
          amount: '0',
          doc_nom: String(b?.acc_rec_d_id ?? ''),
        },
      ],
      confirmed_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
    }
    mockStore.matches = mockStore.matches.filter(
      (x) => x.transaction_id !== txnId || x.state === 'committed' || x.state === 'rejected' || x.state === 'reversed'
    )
    mockStore.matches.push(newMatch)
    return newMatch
  }
  // Auto-match: create suggestions for unmatched txns that have a known mapping,
  // and any existing `suggested` matches → `auto_confirmed`.
  if (method === 'POST' && /\/auto-match$/.test(pathname)) {
    let count = 0
    const now = new Date().toISOString()
    const hasNonTerminal = (txnId: string) =>
      mockStore.matches.some(
        (x) => x.transaction_id === txnId && x.state !== 'rejected' && x.state !== 'reversed'
      )
    for (const t of mockStore.bankTransactions) {
      if (!t.match_invoice_ids || t.match_invoice_ids.length === 0) continue
      if (hasNonTerminal(t.id)) continue
      mockStore.matches.push({
        id: `match-${t.id}`,
        transaction_id: t.id,
        state: 'auto_confirmed',
        match_kind: 'invoice_payment',
        mirage_bank_account_id: 503,
        agent_confidence: 0.95,
        agent_reasoning: `AI matched ${t.counterparty_name} payment to invoice(s) ${t.match_invoice_ids.join(', ')}`,
        counterparts: t.match_invoice_ids.map((invId, idx) => ({
          id: `cp-${t.id}-${idx}`,
          counterpart_kind: 'mirage_open_item',
          dt_account_id: 503,
          ct_account_id: 401,
          amount: String(Math.abs(t.amount) / t.match_invoice_ids!.length),
          doc_nom: invId,
          doc_date: t.booking_ts.slice(0, 10),
        })),
        created_at: now,
      })
      count++
    }
    for (const m of mockStore.matches) {
      if (m.state === 'suggested') {
        m.state = 'auto_confirmed'
        count++
      }
    }
    const jobId = `job-${Date.now()}`
    autoMatchJobs.set(jobId, { matched: count, total: mockStore.bankTransactions.length })
    return { job_id: jobId }
  }
  // Auto-match job status — completes immediately
  if (method === 'GET' && /\/auto-match\/jobs\/[^/]+$/.test(pathname)) {
    const jobId = pathname.split('/').pop()!
    const j = autoMatchJobs.get(jobId) ?? { matched: 0, total: mockStore.bankTransactions.length }
    return {
      job_id: jobId,
      status: 'completed',
      total_transactions: j.total,
      processed_count: j.total,
      matched_count: j.matched,
    }
  }
  // Postings (existing Mirage journal lines) for "Link existing" tab
  if (method === 'GET' && /\/clients\/[^/]+\/accounts\/[^/]+\/postings/.test(pathname)) {
    return mockStore.invoices.slice(0, 4).map((inv, idx) => {
      const amt = Math.abs(inv.extracted.total_amount ?? 0)
      const isIncome = inv.invoice_type === 'sale'
      return {
        acc_rec_d_id: idx + 1,
        acc_rec_id: idx + 1,
        dt_account_id: isIncome ? 503 : 401,
        dt_account_code: isIncome ? '503' : '401',
        ct_account_id: isIncome ? 411 : 503,
        ct_account_code: isIncome ? '411' : '503',
        amount: amt.toFixed(2),
        base_code: 'EUR',
        doc_nom: inv.extracted.invoice_number,
        doc_date: inv.extracted.invoice_date,
        reg_date: inv.extracted.invoice_date,
        counterparty_name: inv.extracted.vendor_name,
        invoice_doc_num: inv.extracted.invoice_number,
        note: `Invoice ${inv.extracted.invoice_number}`,
      }
    })
  }
  // Open items (real backend accepts POST with account_ids body)
  if ((method === 'GET' || method === 'POST') && /\/clients\/[^/]+\/open-items/.test(pathname)) {
    return mockStore.invoices.map((inv, idx) => ({
      apx_id: idx + 1,
      account_id: 401,
      account_code: '401',
      account_name: 'Suppliers',
      counterparty_name: inv.extracted.vendor_name,
      doc_number: inv.extracted.invoice_number,
      doc_date: inv.extracted.invoice_date,
      balance: String(inv.extracted.total_amount ?? 0),
    }))
  }
  // Dashboard endpoints (Go backend)
  if (method === 'GET' && pathname.startsWith('/dashboard/')) {
    return goDashboardHandle(pathname)
  }
  // Confirm/reject/commit/reverse — return synthetic ok match
  return { id: 'match-mock', state: 'confirmed', match_kind: 'invoice_payment', mirage_bank_account_id: 502, counterparts: [], created_at: new Date().toISOString(), transaction_id: '' }
}

function goDashboardHandle(pathname: string): unknown {
  const client = mockStore.clients[0]
  const clientRef = { client_id: client?.id ?? 'client-default', client_name: client?.name ?? 'Гремлинс Лабс ООД' }
  const byStatus = (s: string) => mockStore.invoices.filter((i) => i.status === s).length

  if (pathname.endsWith('/invoice-queues')) {
    const pending = byStatus('review_required') + byStatus('processing') + byStatus('uploaded')
    const ready = byStatus('reviewed')
    const failed = byStatus('failed')
    const grp = (count: number) =>
      count > 0 ? [{ ...clientRef, count }] : []
    return {
      pending_review_total: pending,
      pending_review_top: grp(pending),
      ready_to_export_total: ready,
      ready_to_export_top: grp(ready),
      failed_total: failed,
      failed_top: grp(failed),
    }
  }
  if (pathname.endsWith('/reconciliation-queue')) {
    const unmatched = mockStore.bankTransactions.filter((t) => !t.matched).length
    return {
      unmatched_total: unmatched,
      unmatched_top:
        unmatched > 0
          ? [
              {
                ...clientRef,
                count: unmatched,
                statements: [
                  {
                    statement_id: 'stmt-default',
                    statement_name: 'Bank statement (April 2026)',
                    count: unmatched,
                  },
                ],
              },
            ]
          : [],
    }
  }
  if (pathname.includes('/throughput')) {
    // Deterministic (not random) so totals are stable across renders.
    return Array.from({ length: 30 }, (_, i) => {
      const seed = (i * 7) % 11
      return {
        date: new Date(Date.now() - (29 - i) * 86400000).toISOString().slice(0, 10),
        completed: (seed % 3),
        review_required: ((seed + 1) % 4),
        failed: (seed % 5 === 0) ? 1 : 0,
        reviewed: ((seed + 2) % 3),
      }
    })
  }
  if (pathname.includes('/ai-accuracy')) {
    return {
      auto_approved_pct: 78.4,
      avg_confidence: 0.93,
      top_corrected_fields: [
        { field: 'vendor_name', count: 6 },
        { field: 'invoice_date', count: 4 },
        { field: 'vat_rate', count: 3 },
      ],
      sample_size: mockStore.invoices.length,
    }
  }
  if (pathname.includes('/reconciliation-stats')) {
    return Array.from({ length: 30 }, (_, i) => {
      const total = 4 + ((i * 3) % 7)
      const matched = Math.floor(total * (0.7 + ((i % 4) * 0.05)))
      return {
        date: new Date(Date.now() - (29 - i) * 86400000).toISOString().slice(0, 10),
        matched,
        total,
        match_rate_pct: total > 0 ? (matched / total) * 100 : 0,
      }
    })
  }
  if (pathname.includes('/clients-summary')) {
    const total = mockStore.invoices.length
    const pending = byStatus('review_required') + byStatus('processing') + byStatus('uploaded')
    const ready = byStatus('reviewed')
    return [
      {
        ...clientRef,
        invoices_in_range: total,
        pending_review: pending,
        ready_to_export: ready,
        last_activity: new Date().toISOString(),
      },
    ]
  }
  return []
}

// ---------------------------------------------------------------------------
// ApiClient class — preserves signature of the real client
// ---------------------------------------------------------------------------
class ApiClient {
  baseURL = '/api/v1'

  async get<T>(url: string, _options?: RequestInit): Promise<T> {
    return (await dispatch('GET', url, undefined)) as T
  }
  async post<T>(url: string, data?: unknown, _options?: RequestInit): Promise<T> {
    return (await dispatch('POST', url, data)) as T
  }
  async put<T>(url: string, data?: unknown, _options?: RequestInit): Promise<T> {
    return (await dispatch('PUT', url, data)) as T
  }
  async patch<T>(url: string, data?: unknown, _options?: RequestInit): Promise<T> {
    return (await dispatch('PATCH', url, data)) as T
  }
  async delete<T>(url: string, _options?: RequestInit): Promise<T> {
    return (await dispatch('DELETE', url, undefined)) as T
  }

  /**
   * apiClient.fetch returns a Response-like object. Used by InvoiceProcessing
   * for endpoints that may return blobs (downloads), JSON, or 204s.
   */
  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    const method = ((options.method || 'GET').toUpperCase() as Method)
    const { pathname } = parseUrl(url)

    // Handle invoice PDF downloads — serve the real fixture file from /public/invoices/
    const downloadMatch = pathname.match(/^\/invoices\/([^/]+)\/download$/)
    if (downloadMatch) {
      const inv = mockStore.getInvoice(downloadMatch[1])
      if (inv) {
        const filePath = inv.blob_url || inv.file_path
        const res = await fetch(filePath)
        return res
      }
      return makeJsonResponse(404, { detail: 'not found' })
    }

    // Handle upload — process FormData, kick off a fake lifecycle
    if (method === 'POST' && pathname === '/invoices/upload') {
      const body = options.body as FormData | undefined
      const file = body?.get('file') as File | null
      if (file) {
        const inv = handleUploadedFile(file)
        return makeJsonResponse(200, {
          ok: true,
          job_id: `job-${inv.id}`,
          invoice: {
            id: inv.id,
            original_filename: inv.original_filename,
            status: inv.status,
          },
        })
      }
      return makeJsonResponse(400, { detail: 'No file provided' })
    }

    await simulateLatency()
    try {
      const bodyVal = options.body
        ? typeof options.body === 'string'
          ? JSON.parse(options.body)
          : options.body
        : undefined
      const data = await dispatch(method, url, bodyVal)
      return makeJsonResponse(200, data)
    } catch (e) {
      const err = e as ApiError
      return makeJsonResponse(err.status || 500, { detail: err.message })
    }
  }
}

function makeJsonResponse(status: number, body: unknown): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  })
}

// Upload simulation: uploading -> processing -> review_required (80%) | failed (20%)
const fixtureCycle = ['inv-hetzner-2026-04', 'inv-screenshotone', 'inv-supabase', 'inv-paddle-gb', 'inv-paddle-us', 'inv-paddle-remittance']
let cycleIdx = 0
function handleUploadedFile(file: File): MockInvoice {
  const id = `inv-uploaded-${Date.now()}`
  const fixtureSrc = mockStore.invoices.find((i) => i.id === fixtureCycle[cycleIdx % fixtureCycle.length])
  cycleIdx++
  const blobUrl = URL.createObjectURL(file)
  const inv: MockInvoice = {
    id,
    original_filename: file.name,
    file_path: blobUrl,
    file_size: file.size,
    mime_type: file.type || 'application/pdf',
    status: 'uploading',
    uploaded_at: new Date().toISOString(),
    user_uploaded: true,
    blob_url: blobUrl,
    extracted: { ...(fixtureSrc?.extracted || {}) },
    deal_name_id: fixtureSrc?.deal_name_id,
    account_assignment_id: fixtureSrc?.account_assignment_id,
    overall_confidence: 0.88,
    invoice_type: fixtureSrc?.invoice_type || 'purchase',
  }
  mockStore.addInvoice(inv)
  setTimeout(() => mockStore.updateInvoice(id, { status: 'processing' }), 300)
  setTimeout(() => {
    const ok = Math.random() < 0.8
    mockStore.updateInvoice(id, {
      status: ok ? 'review_required' : 'failed',
      processed_at: new Date().toISOString(),
      error_message: ok ? undefined : 'Mock extraction failed - try another file',
    })
  }, 300 + 3500 + Math.random() * 1500)
  return inv
}

// ---------------------------------------------------------------------------
// Global fetch interceptor for the Go recon backend (localhost:8081)
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  const realFetch = window.fetch.bind(window)
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
    if (url.includes(':8081') || url.includes('/api/v1/reconciliation/') || url.includes('/api/v1/dashboard/')) {
      await simulateLatency()
      const method = ((init?.method || (input instanceof Request ? input.method : 'GET')) as string).toUpperCase()
      const { pathname } = parseUrl(url)
      let parsedBody: unknown = undefined
      const rawBody = init?.body ?? (input instanceof Request ? await input.clone().text() : undefined)
      if (typeof rawBody === 'string' && rawBody.length > 0) {
        try { parsedBody = JSON.parse(rawBody) } catch { /* ignore */ }
      }
      const data = reconBackendHandle(method, pathname, parsedBody)
      return makeJsonResponse(200, data)
    }
    // PDF fixtures from /invoices/ pass through
    return realFetch(input as RequestInfo, init)
  }) as typeof window.fetch
}

export const apiClient = new ApiClient()
export { ApiError }
export default ApiClient

// ---------------------------------------------------------------------------
// Type re-exports (preserve real api.ts signature)
// ---------------------------------------------------------------------------

export interface ReconciliationFilters {
  match_status?: 'matched' | 'unmatched' | 'committed' | 'all'
  client_ids?: string[]
  search?: string
  date_from?: string
  date_to?: string
  skip?: number
  limit?: number
}

export interface UploadBankStatementResponse {
  message: string
  file_id?: string
  job_id?: string
  filename?: string
  status_url?: string
  processing_status?: string
  duplicate?: boolean
  existing_file?: unknown
  checksum?: string
}

export interface ProcessedFileData {
  id: string
  original_filename: string
  mime_type: string
  size_bytes: number | null
  source: string
  ingested_at: string
  client_id: string | null
  client_name: string | null
  transaction_count: number
  status: 'processing' | 'completed' | 'failed'
  has_committed_links: boolean
  committed_link_count: number
}

export interface ProcessedFilesResponse {
  files: ProcessedFileData[]
  total: number
  skip: number
  limit: number
}

export interface FileDeleteCheckResponse {
  can_delete: boolean
  has_committed_links: boolean
  committed_link_count: number
  transaction_count: number
  warnings: string[]
}

export interface FileDeleteResponse {
  deleted: boolean
  file_id: string
  transactions_deleted: number
  warnings: string[]
}

export interface FileReprocessResponse {
  success: boolean
  message: string
  transactions_deleted: number
  warnings: string[]
}

export interface BankStatementProcessingStatus {
  file_id: string
  filename: string
  status: 'processing' | 'completed' | 'failed'
  message: string
  transactions_count: number
  uploaded_at: string
}

export interface TransactionFilters extends ReconciliationFilters {
  amount_min?: number
  amount_max?: number
}

export interface TransactionData {
  id: string
  source: string | null
  description: string | null
  reference: string | null
  amount: number | null
  booking_ts: string | null
  counterparty_name: string | null
  currency: string | null
  client_id: string | null
  client_name: string | null
}

export interface TransactionsResponse {
  transactions: TransactionData[]
  total: number
  company_name: string
}

export interface ClientData {
  id: string
  name: string
  status: string
  context?: string
  system_prompt?: string
  mssql_client_id?: string
  user_id: string
  created_at: string
  updated_at: string
  last_synced?: string
}

export interface ClientsResponse {
  clients: ClientData[]
  total: number
  limit: number
  offset: number
  query?: string
}

export const reconciliationApi = {
  async getTransactions(_filters?: TransactionFilters): Promise<TransactionsResponse> {
    return apiClient.get<TransactionsResponse>('/reconciliation/transactions')
  },
  async uploadBankStatement(file: File, _clientId?: string): Promise<UploadBankStatementResponse> {
    const fd = new FormData()
    fd.append('file', file)
    return apiClient.post<UploadBankStatementResponse>('/reconciliation/upload', fd)
  },
  async getBankStatementProcessingStatus(fileId: string): Promise<BankStatementProcessingStatus> {
    return apiClient.get<BankStatementProcessingStatus>(`/reconciliation/upload/${fileId}/status`)
  },
  async getProcessedFiles(_clientId?: string, _skip?: number, _limit?: number): Promise<ProcessedFilesResponse> {
    return apiClient.get<ProcessedFilesResponse>('/reconciliation/files/detailed')
  },
  async checkFileDeleteSafety(fileId: string): Promise<FileDeleteCheckResponse> {
    return apiClient.get<FileDeleteCheckResponse>(`/reconciliation/files/${fileId}/delete-check`)
  },
  async deleteTransactionFile(fileId: string, _force?: boolean): Promise<FileDeleteResponse> {
    return apiClient.delete<FileDeleteResponse>(`/reconciliation/files/${fileId}`)
  },
  async reprocessDuplicateFile(fileId: string, _force?: boolean): Promise<FileReprocessResponse> {
    return apiClient.post<FileReprocessResponse>(`/reconciliation/files/${fileId}/reprocess`)
  },
}

export const processingApi = {
  async getJobStatus(jobId: string) {
    return apiClient.get<{ job_id: string; status: string; progress: number; current_step: string; error_message?: string }>(`/processing/jobs/${jobId}/status`)
  },
  async getTransactionJobStatus(jobId: string) {
    return apiClient.get<{ job_id: string; status: string; progress: number; current_step: string; error_message?: string }>(`/reconciliation/upload/jobs/${jobId}/status`)
  },
  async getFileStatus(fileId: string) {
    return apiClient.get<BankStatementProcessingStatus>(`/reconciliation/upload/${fileId}/status`)
  },
}

export const clientsApi = {
  async getClients(_query?: string, _limit: number = 1000, _offset: number = 0, _status?: string): Promise<ClientsResponse> {
    return apiClient.get<ClientsResponse>('/clients')
  },
  async getClient(clientId: string) {
    return apiClient.get<{ client: ClientData }>(`/clients/${clientId}`)
  },
  async createClient(clientData: { name: string; status?: string; mssql_client_id?: string }) {
    return apiClient.post<{ message: string; client: ClientData }>('/clients', clientData)
  },
  async updateClient(clientId: string, updateData: Partial<ClientData>) {
    return apiClient.patch<{ message: string; client: ClientData }>(`/clients/${clientId}`, updateData)
  },
  async deleteClient(clientId: string) {
    return apiClient.delete<{ message: string }>(`/clients/${clientId}`)
  },
}

export interface AccAssignment { id: number; description: string; action_type?: number; code?: string; is_active: boolean; is_dma: boolean }
export interface Account { code: string; name: string; category: string; level: number }
export interface DealName { id: number; vat_rate: number; transaction_type: string; description: string; currency_code?: number; valid_from?: string; valid_to?: string; vat_code?: string }
export interface AccAssignmentsResponse { acc_assignments: AccAssignment[] }
export interface AccountsResponse { accounts: Account[] }
export interface DealNamesResponse { deal_names: DealName[] }
export interface UnitOfMeasure { code: string; name: string; print?: string }
export interface UnitsOfMeasureResponse { units_of_measure: UnitOfMeasure[] }

export const erpApi = {
  async getAccAssignments(clientId: string, _actionType?: number): Promise<AccAssignmentsResponse> {
    return apiClient.get<AccAssignmentsResponse>(`/clients/${clientId}/acc-assignment`)
  },
  async getAccounts(): Promise<AccountsResponse> {
    return apiClient.get<AccountsResponse>('/accounts')
  },
  async getDealNames(clientId: string, _options?: { vatRate?: number; actionType?: number }): Promise<DealNamesResponse> {
    return apiClient.get<DealNamesResponse>(`/clients/${clientId}/deal-names`)
  },
  async getUnitsOfMeasure(clientId: string): Promise<UnitsOfMeasureResponse> {
    return apiClient.get<UnitsOfMeasureResponse>(`/clients/${clientId}/units-of-measure`)
  },
}

export interface MirageUser { id: number; username: string; full_name: string; is_active: boolean }
export interface MirageUsersResponse { database_name: string; users: MirageUser[] }
export interface UserData {
  id: string
  email: string
  mirage_username: string | null
  mirage_user_id?: number | null
  full_name: string
  is_active: boolean
  is_superuser: boolean
  role: string
  status: string
  first_login_at?: string | null
  created_at: string
  updated_at: string
}

export const usersApi = {
  async getMirageUsers(): Promise<MirageUsersResponse> {
    return apiClient.get<MirageUsersResponse>('/users/mirage-users')
  },
  async getCurrentUser(): Promise<UserData> {
    return apiClient.get<UserData>('/auth/me')
  },
  async getUser(userId: string): Promise<UserData> {
    return apiClient.get<UserData>(`/users/${userId}`)
  },
  async updateUser(userId: string, updateData: Partial<UserData>) {
    return apiClient.put<{ message: string; user: UserData }>(`/users/${userId}`, updateData)
  },
  async updateUserRole(userId: string, role: string): Promise<UserData> {
    return apiClient.put<UserData>(`/admin/users/${userId}/role`, { role })
  },
}
